import React, { useEffect, useRef, useState, useCallback } from 'react';
import './Second.css';

/* ─── 実績データ ─────────────────────────────── */
const stats = [
  {
    id: 1,
    iconSvg: 'ネットの問い合わせはまれだったけど…',
    value: 2,
    decimals: 0,
    suffix: '倍',
    badge: '運用2ヶ月で',
    label: '問い合わせ件数が増加',
    description: '運用スタートからわずか2ヶ月で問い合わせ数が2倍以上に。適切な広告設計が集客力を大きく底上げします。',
    colorClass: 'card-blue',
  },
  {
    id: 2,
    iconSvg: 'だんだん誰にも見られなくなってたけど…',
    value: 3,
    decimals: 0,
    suffix: '倍',
    badge: '広告配信最適化で',
    label: 'インプレッション数が拡大',
    description: '広告配信の精度を高めることで、狙ったエリア・ターゲット層へのリーチが3倍に拡大。無駄な露出を排除します。',
    colorClass: 'card-purple',
  },
  {
    id: 3,
    iconSvg: '広告って無駄なのかなって思ってたけど…',
    value: 2.7,
    decimals: 1,
    suffix: '倍',
    badge: '同じ広告費で',
    label: 'コンバージョン数が向上',
    description: 'ターゲット精度を上げた配信戦略により、広告費を増やさずにコンバージョンが2.7倍に。費用対効果が大きく改善。',
    colorClass: 'card-pink',
  },
  {
    id: 4,
    iconSvg: '目に見える成果がずっと欲しかった！',
    value: 1.8,
    decimals: 1,
    suffix: '倍',
    badge: '無駄な配信を削減し',
    label: '広告費対効果（ROAS）が改善',
    description: '無駄な配信を徹底排除し、本当に届けたい層に集中投下。広告費対効果（ROAS）が1.8倍に大幅改善しました。',
    colorClass: 'card-orange',
  },
];

/* ─── カウントアップ Hook ─────────────────────── */
function useCountUp(target, decimals = 0, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  const frameRef = useRef(null);

  const run = useCallback(() => {
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = ease * target;
      setCount(parseFloat(current.toFixed(decimals)));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };
    frameRef.current = requestAnimationFrame(step);
  }, [target, decimals, duration]);

  useEffect(() => {
    if (start) {
      run();
    }
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [start, run]);

  return count;
}

/* ─── 各カード ──────────────────────────────── */
const StatCard = ({ stat, index, isVisible }) => {
  const count = useCountUp(stat.value, stat.decimals, 1600 + index * 150, isVisible);
  const displayValue = stat.decimals > 0 ? count.toFixed(stat.decimals) : Math.round(count);

  return (
    <div
      className={`second-card ${stat.colorClass} animate-on-scroll`}
      style={{ animationDelay: `${index * 0.12}s` }}
    >
      {/* アイコン */}
      <div className="second-card-icon">{stat.iconSvg}</div>

      {/* バッジ */}
      <span className="second-card-badge">{stat.badge}</span>

      {/* 数値 */}
      <div className="second-card-value">
        <span className="second-card-number">{displayValue}</span>
        <span className="second-card-suffix">{stat.suffix}</span>
      </div>

      {/* ラベル */}
      <p className="second-card-label">{stat.label}</p>

      {/* 区切り線 */}
      <div className="second-card-divider" />

      {/* 説明 */}
      <p className="second-card-desc">{stat.description}</p>
    </div>
  );
};

/* ─── メインコンポーネント ──────────────────── */
const Second = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="second" ref={sectionRef}>
      {/* 背景装飾 */}
      <div className="second-bg-orb second-bg-orb--1" aria-hidden="true" />
      <div className="second-bg-orb second-bg-orb--2" aria-hidden="true" />
      <div className="second-bg-orb second-bg-orb--3" aria-hidden="true" />

      <div className="second-container">
        {/* ヘッダー */}
        <div className={`second-header ${isVisible ? 'second-header--visible' : ''}`}>
          <span className="second-eyebrow"> </span>
          <h2 className="second-title">
            数字が証明する<br className="sp-only" />
            <span className="second-title-accent">広告の成果</span>
          </h2>
          <p className="second-subtitle">
            広告の知識がなくても大丈夫。私たちの運用で<br />あなたのビジネスに<strong>目に見える変化</strong>をお届けします。
          </p>
        </div>

        {/* カードグリッド */}
        <div className="second-grid">
          {stats.map((stat, i) => (
            <StatCard key={stat.id} stat={stat} index={i} isVisible={isVisible} />
          ))}
        </div>

        {/* フッター注記 */}
        <p className="second-note">
          ※ 数値は運用実績の平均値をもとにした参考値です。業種・予算・配信期間により異なります。
        </p>
      </div>
    </section>
  );
};

export default Second;